import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Modal, Tooltip, message as antMessage } from "antd";
import { motion } from "framer-motion";
import {
  EyeInvisibleOutlined,
  LogoutOutlined,
  WarningFilled,
} from "@ant-design/icons";
import { postData } from "../../api/apiService";
import { ADMIN } from "../../api/endpointMap";
import { decodeJwtPayload, useAuth } from "../../context/AuthContext";
import {
  consumeReturnToPath,
  restoreAdminSession,
} from "../../utils/impersonation";
// restoreAdminSession + consumeReturnToPath used to live here; extracted to
// `utils/impersonation.js` on 04-Jun-2026 so the global 401 interceptor can
// call the same restore logic when the shadow token expires mid-request
// (previously that path wiped both tokens and dumped the admin on /login).

// mm:ss formatter. Minutes have no leading zero; seconds always pad to 2.
// Negative input is clamped to 0 so the visible label never reads "-0:01".
const formatRemaining = (ms) => {
  const total = Math.max(0, Math.floor(ms / 1000));
  const minutes = Math.floor(total / 60);
  const seconds = total % 60;
  return `${minutes}:${String(seconds).padStart(2, "0")}`;
};

// Where the pill's dragged position is remembered, per tab.
const DRAG_POS_KEY = "impersonation_pill_pos";

const ImpersonationBanner = () => {
  const {
    user,
    impersonatingUserId,
    isImpersonating,
    setImpersonatingUserId,
    setUser,
    logout,
  } = useAuth();
  const navigate = useNavigate();
  const [stopping, setStopping] = useState(false);

  // Floating pill: drag box + remembered position. This used to be a
  // full-width sticky bar, which sat on top of the app header and hid the
  // logo and part of the nav. A compact pill that floats clear of the
  // chrome, and that the admin can move if it ever covers something,
  // leaves the underlying page fully usable.
  const dragBoundsRef = useRef(null);
  // sessionStorage, NOT localStorage: the login page clears localStorage on
  // mount, and a remembered position is per-tab anyway.
  const [dragPos] = useState(() => {
    try {
      const saved = JSON.parse(sessionStorage.getItem(DRAG_POS_KEY) || "null");
      if (saved && typeof saved.x === "number" && typeof saved.y === "number") {
        return saved;
      }
    } catch {
      /* malformed entry — fall through to the default corner */
    }
    return { x: 0, y: 0 };
  });

  const persistDragPos = (_event, info) => {
    try {
      const current = JSON.parse(sessionStorage.getItem(DRAG_POS_KEY) || "null");
      const base = current?.x != null ? current : { x: 0, y: 0 };
      sessionStorage.setItem(
        DRAG_POS_KEY,
        JSON.stringify({
          x: base.x + info.offset.x,
          y: base.y + info.offset.y,
        })
      );
    } catch {
      /* storage disabled — the pill simply forgets where it was put */
    }
  };

  // Token expiry derived from the shadow JWT. Re-evaluated whenever the
  // impersonation state flips so refreshes mid-session pick up the right exp.
  const expiryMs = useMemo(() => {
    if (!isImpersonating) return null;
    const token = localStorage.getItem("authToken");
    const payload = decodeJwtPayload(token);
    if (!payload?.exp) return null;
    return Number(payload.exp) * 1000;
  }, [isImpersonating]);

  const [remainingMs, setRemainingMs] = useState(() =>
    expiryMs ? expiryMs - Date.now() : 0
  );

  // Guard against the auto-expire effect firing twice (React 18 strict-mode
  // double-invoke + the same `remainingMs <= 0` condition holding for several
  // ticks before the impersonation state actually flips off).
  const expiredRef = useRef(false);

  // Common teardown for both manual-stop and auto-expire. The order matters:
  //   - manual: shadow token is still valid, so fire the audit ping FIRST
  //     (with the shadow token in place so the BE resolves the target_id
  //     off request.state.impersonating_user_id), then restore.
  //   - expired: shadow token is dead. Sending the audit ping with a dead
  //     token would 401 and trip the global interceptor before restore ran
  //     — that was the root cause of admins getting signed out on expiry.
  //     Skip the ping entirely; the client-side truth already reflects the
  //     stop and the audit row is best-effort.
  const tearDown = useCallback(
    async (kind /* "manual" | "expired" */) => {
      if (kind === "manual") {
        try {
          await postData(ADMIN.IMPERSONATE_STOP, {});
        } catch {
          /* audit ping is best-effort; never strand admin in impersonated view */
        }
      }

      const target = consumeReturnToPath();
      // Await — the restore may need to mint a fresh admin access token
      // (the stashed one outlives its 15-minute life during any real
      // impersonation session; see restoreAdminSession).
      const restored = await restoreAdminSession();
      setImpersonatingUserId(null);

      if (restored) {
        setUser(restored);
        if (kind === "expired") {
          antMessage.warning("Impersonation session expired.");
        } else {
          antMessage.success("Impersonation stopped.");
        }
        navigate(target);
      } else {
        antMessage.warning(
          kind === "expired"
            ? "Impersonation session expired. Please sign in again."
            : "Original admin session unavailable. Please sign in again."
        );
        await logout();
      }
    },
    [navigate, setImpersonatingUserId, setUser, logout]
  );

  // 1-second countdown tick. Skipped when the tab is hidden — `setInterval`
  // is throttled by browsers in background tabs anyway, but this keeps the
  // banner from doing pointless re-renders when nobody's looking.
  useEffect(() => {
    if (!isImpersonating || !expiryMs) return undefined;

    let intervalId = null;

    const recompute = () => {
      const left = expiryMs - Date.now();
      setRemainingMs(left);
      if (left <= 0 && !expiredRef.current) {
        expiredRef.current = true;
        if (intervalId !== null) {
          clearInterval(intervalId);
          intervalId = null;
        }
        tearDown("expired");
      }
    };

    const start = () => {
      if (intervalId !== null) return;
      recompute();
      intervalId = setInterval(recompute, 1000);
    };

    const stop = () => {
      if (intervalId !== null) {
        clearInterval(intervalId);
        intervalId = null;
      }
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        stop();
      } else {
        // Recompute immediately so a long suspension snaps to the correct
        // remaining time instead of waiting up to a second.
        start();
      }
    };

    if (document.visibilityState === "visible") {
      start();
    } else {
      // Even when hidden, evaluate once so an already-expired token tears
      // down on banner mount instead of waiting for the tab to come back.
      recompute();
    }
    document.addEventListener("visibilitychange", onVisibilityChange);

    return () => {
      stop();
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [isImpersonating, expiryMs, tearDown]);

  // Reset the expired-once latch whenever a fresh impersonation starts.
  useEffect(() => {
    if (!isImpersonating) {
      expiredRef.current = false;
    }
  }, [isImpersonating]);

  if (!isImpersonating) return null;

  const targetLabel =
    user?.email || user?.full_name || impersonatingUserId || "target user";

  // Display copy. When remainingMs is non-positive we say "now…" instead of
  // "0:00" since the auto-stop is already in flight.
  const countdownLabel =
    expiryMs == null
      ? "-"
      : remainingMs <= 0
        ? "now…"
        : formatRemaining(remainingMs);

  const stopImpersonating = async () => {
    setStopping(true);
    await tearDown("manual");
    setStopping(false);
  };

  const confirmStop = () => {
    Modal.confirm({
      title: "Stop impersonating?",
      icon: <WarningFilled style={{ color: "#f87171" }} />,
      content: `You will return to your admin session and leave ${targetLabel}'s view.`,
      okText: "Stop",
      // Read-only mode disables every button on the page, and this dialog's
      // buttons render into a portal on <body> — which carries that class —
      // so without the exemption the admin could open this dialog and then
      // not be able to confirm or dismiss it. Same treatment the logout
      // dialog already gives its buttons.
      okButtonProps: {
        danger: true,
        loading: stopping,
        className: "exempt-from-readonly",
      },
      cancelButtonProps: { className: "exempt-from-readonly" },
      onOk: stopImpersonating,
    });
  };

  return (
    // The wrapper spans the viewport ONLY to give the drag a constraint box.
    // `pointer-events-none` keeps it from intercepting clicks meant for the
    // page underneath; the pill itself opts back in.
    <div
      ref={dragBoundsRef}
      className="fixed inset-0 z-[9999] pointer-events-none"
      aria-hidden={false}
    >
      <motion.div
        role="alert"
        drag
        dragConstraints={dragBoundsRef}
        dragElastic={0}
        dragMomentum={false}
        initial={false}
        style={{ x: dragPos.x, y: dragPos.y }}
        onDragEnd={persistDragPos}
        className={
          "pointer-events-auto absolute bottom-6 right-6 cursor-grab " +
          "active:cursor-grabbing select-none rounded-full bg-red-600 " +
          "text-white shadow-xl ring-1 ring-black/10 " +
          "flex items-center gap-3 pl-3 pr-2 py-1.5 max-w-[92vw]"
        }
      >
        <Tooltip title="Drag to move. Your admin identity remains the source of truth in the audit log.">
          <span className="flex items-center gap-2 min-w-0">
            <EyeInvisibleOutlined className="text-base shrink-0" />
            <span className="text-sm font-medium truncate max-w-[38vw] sm:max-w-xs">
              {targetLabel}
            </span>
            <span
              aria-live="polite"
              className="hidden sm:inline text-white/90 text-xs whitespace-nowrap font-mono"
            >
              {countdownLabel}
            </span>
          </span>
        </Tooltip>
        {/* `exempt-from-readonly` is REQUIRED, not cosmetic. An impersonated
            session is usually read-only, and read-only mode sets
            `pointer-events: none` + a dimmed opacity on every button that
            lacks this class — which left the admin looking at a greyed-out
            Stop button that swallowed clicks, with no way out of the
            impersonated session but to wait out the countdown. An escape
            hatch must never be disabled by the mode it escapes from; the
            logout button is exempted for the same reason. */}
        <Button
          size="small"
          shape="round"
          icon={<LogoutOutlined />}
          onClick={confirmStop}
          loading={stopping}
          // A click that ends a drag must not also fire the button.
          onMouseDown={(e) => e.stopPropagation()}
          onPointerDown={(e) => e.stopPropagation()}
          className="exempt-from-readonly shrink-0 !bg-white !text-red-600 !border-white hover:!bg-red-50"
        >
          Stop
        </Button>
      </motion.div>
    </div>
  );
};

export default ImpersonationBanner;
